import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import useInactivityTimeout from "./hooks/useInactivityTimeout";
import LegalDisclaimer from "./components/LegalDisclaimer";
import AccessTracker from "./components/AccessTracker";
import CookieConsentBanner from "./components/CookieConsentBanner";
import PrivacyConsentModal from "./components/PrivacyConsentModal";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DemoLoopPage from "./pages/DemoLoopPage";
import DossierPage from "./pages/DossierPage";
import GranadaLandingPage from "./pages/GranadaLandingPage";
import MacrosPage from "./pages/admin/MacrosPage";
import SystemResetPage from "./pages/admin/SystemResetPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AdminPinPage from "./pages/AdminPinPage";

// Legal Pages
import AvisoLegalPage from "./pages/legal/AvisoLegalPage";
import PrivacidadPage from "./pages/legal/PrivacidadPage";
import CookiesPage from "./pages/legal/CookiesPage";
import TerminosUsoPage from "./pages/legal/TerminosUsoPage";
import BloqueoSuspensionPage from "./pages/legal/BloqueoSuspensionPage";
import ExencionResponsabilidadPage from "./pages/legal/ExencionResponsabilidadPage";
import TerminosVentaPage from "./pages/legal/TerminosVentaPage";
import DashboardPage from "./pages/admin/DashboardPage";
import ClientsPage from "./pages/admin/ClientsPage";
import ClientDetailPage from "./pages/admin/ClientDetailPage";
import PlansPage from "./pages/admin/PlansPage";
import PlanAuditPage from "./pages/admin/PlanAuditPage";
import VideoLinkingPage from "./pages/admin/VideoLinkingPage";
import VideoHealthPage from "./pages/admin/VideoHealthPage";
import VideoToolsPage from "./pages/admin/VideoToolsPage";
import LibraryResetPage from "./pages/admin/LibraryResetPage";
import AdminAutomationPage from "./pages/admin/AdminAutomationPage";
import TrainerAutomationPage from "./pages/trainer/TrainerAutomationPage";
import PlanBuilderPage from "./pages/admin/PlanBuilderPage";
import ExercisesPage from "./pages/admin/ExercisesPage";
import PaymentsPage from "./pages/admin/PaymentsPage";
import DailyTipsPage from "./pages/admin/DailyTipsPage";
import PildorasProgresivasPage from "./pages/admin/PildorasProgresivasPage";
import ModuloNutricionPage from "./pages/admin/ModuloNutricionPage";
import AdminResourceCenterPage from "./pages/admin/AdminResourceCenterPage";
import AccessControlPage from "./pages/admin/AccessControlPage";
import GuardianAdminPage from "./pages/admin/GuardianAdminPage";
import AdminTrainersPage from "./pages/admin/AdminTrainersPage";
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientPlanView from "./pages/client/ClientPlanView";
import ClientCurrentSession from "./pages/client/ClientCurrentSession";
import ResourceCenterPage from "./pages/client/ResourceCenterPage";
import NutricionPage from "./pages/client/NutricionPage";
import DisenaEntrenoPage from "./pages/client/DisenaEntrenoPage";
import GuardianPage from "./pages/client/GuardianPage";
import TrainerDashboard from "./pages/trainer/TrainerDashboard";
import TrainerClientsPage from "./pages/trainer/TrainerClientsPage";
import TrainerClientDetailPage from "./pages/trainer/TrainerClientDetailPage";
import TrainerPlansPage from "./pages/trainer/TrainerPlansPage";
import TrainerPlanBuilder from "./pages/trainer/TrainerPlanBuilder";
import TrainerExercisesPage from "./pages/trainer/TrainerExercisesPage";
import TrainerPildorasPage from "./pages/trainer/TrainerPildorasPage";

// Chat pages
import ClientChatPage from "./pages/client/ClientChatPage";
import TrainerChatPage from "./pages/trainer/TrainerChatPage";

// Pricing & pagos
import PricingPage from "./pages/PricingPage";
import PagoExitoPage from "./pages/PagoExitoPage";
import PagoCanceladoPage from "./pages/PagoCanceladoPage";
import MySubscriptionPage from "./pages/client/MySubscriptionPage";

// Inactivity Monitor Component
const InactivityMonitor = ({ children }) => {
  useInactivityTimeout();
  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading, adminPinVerified } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Admin routes require PIN verification
  if (requiredRole === 'admin' && user.role === 'admin' && !adminPinVerified) {
    return <Navigate to="/admin-pin" replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    if (user.role === 'trainer') {
      return <Navigate to="/trainer" replace />;
    }
    return <Navigate to="/portal" replace />;
  }

  return children;
};

// Public Route - Redirects if already logged in
const PublicRoute = ({ children }) => {
  const { user, loading, adminPinVerified } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    if (user.role === 'admin') {
      // Admin needs PIN verification first
      if (!adminPinVerified) {
        return <Navigate to="/admin-pin" replace />;
      }
      return <Navigate to="/admin" replace />;
    }
    if (user.role === 'trainer') {
      return <Navigate to="/trainer" replace />;
    }
    return <Navigate to="/portal" replace />;
  }

  return children;
};

// PIN Route - Only for logged-in admins who haven't verified PIN yet
const PinRoute = ({ children }) => {
  const { user, loading, adminPinVerified } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in - go to login
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Not admin - go to client portal
  if (user.role !== 'admin') {
    return <Navigate to="/portal" replace />;
  }

  // Already verified PIN - go to admin dashboard
  if (adminPinVerified) {
    return <Navigate to="/admin" replace />;
  }

  // Admin without PIN verification - show PIN page
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/demo-loop" element={<DemoLoopPage />} />
      <Route path="/dossier" element={<DossierPage />} />
      <Route path="/granada" element={<GranadaLandingPage />} />
      <Route path="/feria" element={<GranadaLandingPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Legal Pages - Public */}
      <Route path="/legal/aviso-legal" element={<AvisoLegalPage />} />
      <Route path="/legal/privacidad" element={<PrivacidadPage />} />
      <Route path="/legal/cookies" element={<CookiesPage />} />
      <Route path="/legal/terminos-uso" element={<TerminosUsoPage />} />
      <Route path="/legal/bloqueo-suspension" element={<BloqueoSuspensionPage />} />
      
      {/* Pricing y pagos (públicos para que se vean desde landing) */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/pago/exito" element={<PagoExitoPage />} />
      <Route path="/pago/cancelado" element={<PagoCanceladoPage />} />
      <Route path="/legal/exencion-responsabilidad" element={<ExencionResponsabilidadPage />} />
      <Route path="/legal/terminos-venta" element={<TerminosVentaPage />} />

      {/* Admin PIN Verification Route */}
      <Route path="/admin-pin" element={<PinRoute><AdminPinPage /></PinRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><DashboardPage /></ProtectedRoute>} />
      <Route path="/admin/clients" element={<ProtectedRoute requiredRole="admin"><ClientsPage /></ProtectedRoute>} />
      <Route path="/admin/macros" element={<ProtectedRoute requiredRole="admin"><MacrosPage /></ProtectedRoute>} />
      <Route path="/admin/system-reset" element={<ProtectedRoute requiredRole="admin"><SystemResetPage /></ProtectedRoute>} />
      <Route path="/admin/clients/:clientId" element={<ProtectedRoute requiredRole="admin"><ClientDetailPage /></ProtectedRoute>} />
      <Route path="/admin/plans" element={<ProtectedRoute requiredRole="admin"><PlansPage /></ProtectedRoute>} />
      <Route path="/admin/plan-audit" element={<ProtectedRoute requiredRole="admin"><PlanAuditPage /></ProtectedRoute>} />
      <Route path="/admin/video-linking" element={<ProtectedRoute requiredRole="admin"><VideoLinkingPage /></ProtectedRoute>} />
      <Route path="/admin/video-health" element={<ProtectedRoute requiredRole="admin"><VideoHealthPage /></ProtectedRoute>} />
      <Route path="/admin/video-tools" element={<ProtectedRoute requiredRole="admin"><VideoToolsPage /></ProtectedRoute>} />
      <Route path="/admin/library-reset" element={<ProtectedRoute requiredRole="admin"><LibraryResetPage /></ProtectedRoute>} />
      <Route path="/admin/automation" element={<ProtectedRoute requiredRole="admin"><AdminAutomationPage /></ProtectedRoute>} />
      <Route path="/admin/plans/new" element={<ProtectedRoute requiredRole="admin"><PlanBuilderPage /></ProtectedRoute>} />
      <Route path="/admin/plans/:id" element={<ProtectedRoute requiredRole="admin"><PlanBuilderPage /></ProtectedRoute>} />
      <Route path="/admin/exercises" element={<ProtectedRoute requiredRole="admin"><ExercisesPage /></ProtectedRoute>} />
      <Route path="/admin/payments" element={<ProtectedRoute requiredRole="admin"><PaymentsPage /></ProtectedRoute>} />
      <Route path="/admin/tips" element={<ProtectedRoute requiredRole="admin"><DailyTipsPage /></ProtectedRoute>} />
      <Route path="/admin/pildoras" element={<ProtectedRoute requiredRole="admin"><PildorasProgresivasPage /></ProtectedRoute>} />
      <Route path="/admin/nutricion" element={<ProtectedRoute requiredRole="admin"><ModuloNutricionPage /></ProtectedRoute>} />
      <Route path="/admin/recursos" element={<ProtectedRoute requiredRole="admin"><AdminResourceCenterPage /></ProtectedRoute>} />
      <Route path="/admin/access-control" element={<ProtectedRoute requiredRole="admin"><AccessControlPage /></ProtectedRoute>} />
      <Route path="/admin/guardian" element={<ProtectedRoute requiredRole="admin"><GuardianAdminPage /></ProtectedRoute>} />
      <Route path="/admin/trainers" element={<ProtectedRoute requiredRole="admin"><AdminTrainersPage /></ProtectedRoute>} />

      {/* Trainer Routes */}
      <Route path="/trainer" element={<ProtectedRoute requiredRole="trainer"><TrainerDashboard /></ProtectedRoute>} />
      <Route path="/trainer/clients" element={<ProtectedRoute requiredRole="trainer"><TrainerClientsPage /></ProtectedRoute>} />
      <Route path="/trainer/clients/:clientId" element={<ProtectedRoute requiredRole="trainer"><TrainerClientDetailPage /></ProtectedRoute>} />
      <Route path="/trainer/plans" element={<ProtectedRoute requiredRole="trainer"><TrainerPlansPage /></ProtectedRoute>} />
      <Route path="/trainer/plans/new" element={<ProtectedRoute requiredRole="trainer"><TrainerPlanBuilder /></ProtectedRoute>} />
      <Route path="/trainer/plans/:id" element={<ProtectedRoute requiredRole="trainer"><TrainerPlanBuilder /></ProtectedRoute>} />
      <Route path="/trainer/exercises" element={<ProtectedRoute requiredRole="trainer"><TrainerExercisesPage /></ProtectedRoute>} />
      <Route path="/trainer/pildoras" element={<ProtectedRoute requiredRole="trainer"><TrainerPildorasPage /></ProtectedRoute>} />
      <Route path="/trainer/automation" element={<ProtectedRoute requiredRole="trainer"><TrainerAutomationPage /></ProtectedRoute>} />
      <Route path="/trainer/chat" element={<ProtectedRoute requiredRole="trainer"><TrainerChatPage /></ProtectedRoute>} />

      {/* Client Routes */}
      <Route path="/portal" element={<ProtectedRoute requiredRole="client"><ClientDashboard /></ProtectedRoute>} />
      <Route path="/portal/session" element={<ProtectedRoute requiredRole="client"><ClientCurrentSession /></ProtectedRoute>} />
      <Route path="/portal/plan/:planId" element={<ProtectedRoute requiredRole="client"><ClientPlanView /></ProtectedRoute>} />
      <Route path="/portal/recursos" element={<ProtectedRoute requiredRole="client"><ResourceCenterPage /></ProtectedRoute>} />
      <Route path="/portal/nutricion" element={<ProtectedRoute requiredRole="client"><NutricionPage /></ProtectedRoute>} />
      <Route path="/portal/disena-entreno" element={<ProtectedRoute requiredRole="client"><DisenaEntrenoPage /></ProtectedRoute>} />
      <Route path="/client/guardian" element={<ProtectedRoute requiredRole="client"><GuardianPage /></ProtectedRoute>} />
      <Route path="/portal/chat" element={<ProtectedRoute requiredRole="client"><ClientChatPage /></ProtectedRoute>} />
      <Route path="/portal/suscripcion" element={<ProtectedRoute requiredRole="client"><MySubscriptionPage /></ProtectedRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function GlobalOverlays() {
  // Sin modales legales/cookies en las pantallas de stand e impresión
  const location = useLocation();
  if (
    location.pathname.startsWith('/demo-loop') ||
    location.pathname.startsWith('/dossier') ||
    location.pathname.startsWith('/granada') ||
    location.pathname.startsWith('/feria')
  ) {
    return null;
  }
  return (
    <>
      <LegalDisclaimer />
      <PrivacyConsentModal />
      <CookieConsentBanner />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <GlobalOverlays />
            <AccessTracker />
            <InactivityMonitor>
              <AppRoutes />
            </InactivityMonitor>
            <Toaster position="top-right" richColors />
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
